import { graphql } from './index'
import type { ResultOf } from './index'

export const GET_ME = graphql(`
  query GetMe {
    me {
      id
      email
      firstName
      lastName
      fullName
      superAdmin
      heartAbilities {
        identifier
      }
    }
  }
`)

export const VixieesUserFragment = graphql(`
  fragment VixieesUserFragment on VixieesUser @_unmask {
    id
    firstName
    lastName
    fullName
    email
    superAdmin
    createdAt
    heartAbilities {
      identifier
    }
  }
`)

export type VixieesUserType = ResultOf<typeof VixieesUserFragment>

export const GET_HEART_ABILITIES = graphql(`
  query GetHeartAbilities {
    heartAbilities {
      identifier
      description
    }
  }
`)

export const GET_VIXIEES_USERS = graphql(
  `
    query GetVixieesUsers($first: Int, $after: String, $words: String) {
      vixieesUsers(first: $first, after: $after, words: $words) {
        edges {
          node {
            ...VixieesUserFragment
          }
          cursor
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
        totalCount
      }
    }
  `,
  [VixieesUserFragment],
)

export const AccountFragment = graphql(`
  fragment AccountFragment on Account @_unmask {
    id
    name
    internalName
    active
    demo
    verified
    recordingEnabled
    aiEnabled
    crmIntegrated
    customerSuccessNumber
    timeZone
    mainPhoneNumber
    defaultCountryCode
    defaultCurrency
    createdAt
    twilioSid
    stripeCustomerId
    stripeCustomerExists
    stripeSubscriptionStatus
    stripeStatusSyncedAt
  }
`)

export const GET_ACCOUNTS = graphql(
  `
    query GetAccounts(
      $first: Int
      $after: String
      $active: Boolean
      $stripeCustomerExists: Boolean
      $stripeSubscriptionStatus: StripeSubscriptionStatus
      $words: String
    ) {
      accounts(
        first: $first
        after: $after
        active: $active
        stripeCustomerExists: $stripeCustomerExists
        stripeSubscriptionStatus: $stripeSubscriptionStatus
        words: $words
      ) {
        edges {
          node {
            ...AccountFragment
          }
          cursor
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
        totalCount
      }
    }
  `,
  [AccountFragment],
)

export const GET_PRICING_TEMPLATES = graphql(`
  query GetPricingTemplates {
    pricingTemplates {
      id
      country
      currency
      basicLicensePrice
      p2LicensePrice
      p3LicensePrice
      p4MinutesPrice
      p6MinutesPrice
      p4AiPrice
      p6AiPrice
      p4RecordingPrice
      p6RecordingPrice
    }
  }
`)

export const GET_CALL_PHONES = graphql(`
  query GetCallPhones(
    $first: Int
    $after: String
    $provider: String
    $countryCode: String
    $accountId: ID
    $verificationStatus: CallPhoneVerificationStatus
    $words: String
  ) {
    callPhones(
      first: $first
      after: $after
      provider: $provider
      countryCode: $countryCode
      accountId: $accountId
      verificationStatus: $verificationStatus
      words: $words
    ) {
      edges {
        node {
          id
          name
          number
          provider
          countryCode
          verificationStatus
          verificationFailedReason
          outboundOnly
          account {
            id
            name
          }
          phoneNumberRequest {
            id
            country
            prefix
            createdAt
          }
        }
        cursor
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      totalCount
    }
  }
`)

export const GET_PHONE_NUMBER_REQUESTS = graphql(`
  query GetPhoneNumberRequests(
    $first: Int
    $after: String
    $provider: String
    $country: String
    $accountId: ID
    $status: String
    $words: String
  ) {
    phoneNumberRequests(
      first: $first
      after: $after
      provider: $provider
      country: $country
      accountId: $accountId
      status: $status
      words: $words
    ) {
      edges {
        node {
          id
          quantity
          country
          prefix
          specialRequirements
          provider
          status
          createdAt
          contractUrl
          contractSignedAt
          failedReason
          account {
            id
            name
          }
          callPhones {
            id
          }
        }
        cursor
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      totalCount
    }
  }
`)

export const AccountOfferFragment = graphql(`
  fragment AccountOfferFragment on AccountOffer @_unmask {
    id
    prospectEmail
    firstName
    lastName
    prospectCompanyName
    planPrice
    paymentPeriod
    recordingEnabled
    aiEnabled
    salesConsultingIncluded
    salesConsultingAmount
    subscriptions {
      country
      licenses
      minutesPlan
    }
    totalLicenses
    status
    sent
    sentAt
    expired
    expiresAt
    createdAt
    signupUrl
  }
`)

export const GET_ACCOUNT_OFFERS = graphql(
  `
    query GetAccountOffers(
      $first: Int
      $after: String
      $status: String
      $words: String
    ) {
      accountOffers(
        first: $first
        after: $after
        status: $status
        words: $words
      ) {
        edges {
          node {
            ...AccountOfferFragment
          }
          cursor
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
        totalCount
      }
    }
  `,
  [AccountOfferFragment],
)

export const GET_OFFER_EMAIL_CONFLICTS = graphql(`
  query GetOfferEmailConflicts($email: String!) {
    offerEmailConflicts(email: $email) {
      accountId
      accountName
      offerId
      pendingSignup
      replacedOnCreate
    }
  }
`)
